import {
	setStoreMusicRef,
	setStoreMusicPlayState,
	pushToMediaStack,
	popFromMediaStack,
	clearMediaStack,
	setPersistOnStateChange,
} from './actions';
import { createSlice } from '@reduxjs/toolkit';
import { todoMessage } from '@wsc/utils/consoleHelpers';

/**
 * Initial state for media controller slice
 *
 * @property {Object} initialState
 * @property {HTMLAudioElement | React.Ref} storeMusicRef - Reference to the store music element
 * @property {boolean} storeMusicPlayState - Whether the store music is playing or not
 * @property {boolean} storeMusicPrevPlayState - Whether the store music was playing or not before the user toggled it
 * @property {Array<HTMLAudioElement | React.Ref>} mediaStack - Stack of media elements that are currently playing or paused.
 * @property {boolean} persistOnSceneChange - Whether the media stack should persist on scene change or not.
 *
 *
 * @default
 * <pre>
 *   {
 *   storeMusicRef: null,
 *   storeMusicPlayState: false,
 *   storeMusicPrevPlayState: false,
 *   mediaStack: [],
 *   persistOnSceneChange: false,
 *   }
 * </pre>
 *
 *
 * @see https://redux-toolkit.js.org/api/createSlice
 * @see https://redux-toolkit.js.org/api/createReducer
 */
const initialState = {
	/**
	 * @type {HTMLAudioElement | React.Ref}
	 * @description Reference to the store music element
	 * @default null
	 */
	storeMusicRef: null,

	/**
	 * @type {boolean}
	 * @description Whether the store music is playing or not
	 * @default false
	 */
	storeMusicPlayState: false,

	/**
	 * @type {boolean}
	 * @description Whether the store music was playing or not before the user toggled it
	 * @default false
	 */
	storeMusicPrevPlayState: false,

	/**
	 * @type {Array<HTMLAudioElement | React.Ref>}
	 * @description Stack of media elements that are currently playing or paused.
	 * @default []
	 */
	mediaStack: [],

	/**
	 * @type {boolean}
	 * @description Whether the media stack should persist on scene change or not.
	 * @default false
	 */
	persistOnSceneChange: false,

	/**
	 * @description Actions to change the state of media controller slice exposed so that other components can use them.
	 */
	setStoreMusicRef,
	setStoreMusicPlayState,
	pushToMediaStack,
	popFromMediaStack,
	clearMediaStack,
	setPersistOnStateChange,
};

const UNSUPPORTED_TAGS = ['IFRAME'];

/**
 * @description Pause the media element referenced by ref
 * @param ref
 * @returns {*}
 */
const pauseMediaRef = (ref) => {
	if (UNSUPPORTED_TAGS.includes(ref?.current?.tagName)) {
		return;
	}

	if (ref instanceof Element) {
		if (ref.paused === false) {
			ref?.pause();
		}
		return;
	}

	if (ref?.current === null || ref?.current === undefined) {
		return;
	}

	if (ref?.current instanceof Element) {
		if (ref.current.paused === false) {
			ref?.current?.pause();
		}
		return;
	}

	// ref is a react-player object
	if (ref?.current?.getInternalPlayer) {
		return ref?.current?.getInternalPlayer().pause();
	}
};

/**
 * @description Play the media element referenced by ref
 * @param ref
 * @returns {void|undefined|Promise<void>}
 */
const playMediaRef = (ref) => {
	if (UNSUPPORTED_TAGS.includes(ref?.current?.tagName)) {
		return;
	}

	if (ref instanceof Element) {
		if (ref.paused) {
			return ref?.play();
		}
		return;
	}

	if (!ref?.current) {
		return;
	}

	if (ref?.current instanceof Element) {
		return ref?.current?.play();
	}

	// ref is a react-player object
	if (ref?.current?.getActivePlayer) {
		return ref?.current
			?.getActivePlayer()
			?.canPlay(ref?.current?.props?.url);
	}
};

/**
 * @description Redux slice for media controller.
 * Define reducers and actions here.
 *
 *<ul>
 * <li> Define any new reducers in the `reducers` object </li>
 * <li> @deprecated approach for defining reducers.
 *   <ul>
 * 	 	<li>`slice.extraReducers` is used to define reducers that listen to actions defined in `./actions.jsx`.</li>
 * 	 	<li>Old reducers are defined in `actions.jsx` and that pattern is deprecated when migrated to using `createSlice`</li>
 * 	 	<li>It is only kept in this file for backwards compatibility for older components.</li>
 * 	 </ul>
 * 	 </li>
 *</ul>
 *
 * @see https://redux-toolkit.js.org/api/createSlice
 */
const slice = createSlice({
	name: 'mediaController',
	initialState,
	reducers: {
		/**
		 * @description Define any new reducers in
		 */
	},
	extraReducers: (builder) => {
		/**
		 * @description Updates the store music reference
		 */
		builder.addCase(setStoreMusicRef, (state, action) => {
			state.storeMusicRef = action.payload;
		});

		/**
		 *
		 * @description Updates `state.storeMusicPlayState` and `state.storeMusicPrevPlayState`.
		 *
		 * `storeMusicPrevPlayState` is updated only if `userToggled` is true.
		 *
		 * If `userToggled` is false, then `storeMusicPlayState` is updated only if `action.payload.playState` is true.
		 *
		 * @param {Object} action - Action with data to update
		 * @param {boolean} action.payload.playState - Whether the store music is playing or not
		 * @param {boolean} action.payload.userToggled - Whether the user toggled the store music or not
		 *
		 */
		builder.addCase(setStoreMusicPlayState, (state, action) => {
			if (action.payload.userToggled) {
				state.storeMusicPlayState = action.payload.playState;
				state.storeMusicPrevPlayState = action.payload.playState;
				return;
			}
			state.storeMusicPlayState =
				action.payload.playState && state.storeMusicPrevPlayState;
		});

		/**
		 * @description Pushes the media element referenced by `action.payload` to the `top` media stack.
		 *
		 * - If the media stack is not empty, then the media element at the top of the stack is paused.
		 * - The media element referenced by `action.payload` is added to the top of the stack.
		 * - The media element referenced by `action.payload` is played.
		 *
		 * @param {Object} action - Action with data to update
		 * @param {HTMLAudioElement | React.Ref} action.payload - Reference to the media element
		 *
		 */
		builder.addCase(pushToMediaStack, (state, action) => {
			if (state.mediaStack.length > 0) {
				pauseMediaRef(state.mediaStack[state.mediaStack.length - 1]);
			}
			playMediaRef(action.payload);
			state.mediaStack.push(action.payload);
		});

		builder.addCase(setPersistOnStateChange, (state, action) => {
			todoMessage(
				'setPersistOnStateChange action: If you see this message, please inform @yash [yash@obsessvr.com]',
			);
			state.persistOnSceneChange = action.payload;
		});

		/**
		 * @description Pops the media element at the top of the media stack.
		 * - If the media stack is empty, then `state.storeMusicPlayState` is updated to `state.storeMusicPrevPlayState`.
		 * - If the media stack is not empty, then the media element at the top of the stack is paused.
		 * - The media element at the top of the stack is removed from the stack.
		 * - If the media stack is not empty, then the media element at the top of the stack is played.
		 */
		builder.addCase(popFromMediaStack, (state) => {
			if (state.mediaStack.length <= 0) {
				state.storeMusicPlayState = state.storeMusicPrevPlayState;
				return;
			}
			const popped = state.mediaStack.pop();
			pauseMediaRef(popped);

			if (state.mediaStack.length > 0) {
				playMediaRef(state.mediaStack[state.mediaStack.length - 1]);
			}
		});

		/**
		 * @description Pauses all media elements in the media stack.
		 * - Pauses all media elements in the media stack.
		 * - Clears the media stack.
		 * - Updates `state.mediaStack` to `[]`.
		 */
		builder.addCase(clearMediaStack, (state) => {
			state.mediaStack.forEach((ref) => pauseMediaRef(ref));
			state.mediaStack = [];
		});
	},
});

export default slice.reducer;
